import React from 'react'
import {
    Button,
    Divider,
    Flex,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {defineMessage, useIntl, FormattedMessage} from 'react-intl'
import ProductRegistrationHeader from './partials/product-registration-header'
import ProductRegistrationStepper from './partials/product-registration-stepper'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useAuthHelper, AuthHelpers} from '@salesforce/commerce-sdk-react'
import routingObject from '../../utils/static-routes'
import {getMrtApiBasepath} from '../../ssr/helpers'
import {useLocation} from 'react-router'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'

const ProductRegistrationAccount = () => {
    const styles = useMultiStyleConfig('ProductRegistration', {variant: 'productSearch'})
    const loginStyles = useMultiStyleConfig('ProductRegistrationLogin', {variant: 'account'})
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const navigate = useNavigation()
    const logout = useAuthHelper(AuthHelpers.Logout)
    const queryParams =
        typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search)
            : new URLSearchParams()

    const productId = queryParams.get('code')

    const location = useLocation()
    const {customParams} = getConfig()
    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const intl = useIntl()
    const productRegistrationTitleMessage = defineMessage({
        id: 'product_registration.title',
        defaultMessage: 'Register your product',
    })

    const productRegistrationSubtitleMessage = defineMessage({
        id: 'product_registration.subtitle',
        defaultMessage: 'Register your product to unlock a world of benefits.',
    })
    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: intl.formatMessage(productRegistrationTitleMessage),
        description: intl.formatMessage(productRegistrationSubtitleMessage),
        url: currentPath,
    }

    const handleButtonClick = () => {
        navigate(`${routingObject.PRODUCT_REGISTRATION_PATH}/detail?code=${productId}`)
    }
    const handleLogout = async () => {
        // Preserve selectedProduct from localStorage after logout
        let selectedProductValue = null
        if (typeof window !== 'undefined') {
            selectedProductValue = localStorage.getItem('selectedProduct')
        }

        await logout.mutateAsync()

        const returnUrl = `${routingObject.PRODUCT_REGISTRATION_PATH}/user-detail?code=${productId}`

        const endpoint = origin + getMrtApiBasepath('/logout')
        await fetch(`${endpoint}`).then(() => {
            setTimeout(() => {
                // Restore selectedProduct after Clear-Site-Data clears storage
                if (typeof window !== 'undefined' && selectedProductValue !== null) {
                    localStorage.setItem('selectedProduct', selectedProductValue)
                }
                navigate(returnUrl)
            }, 500)
        })
    }

    return (
        <Flex {...styles.container} layerStyle={'page'}>
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            <Flex {...styles.headerStepperContainer}>
                <ProductRegistrationHeader index={2} variant={'product'} />
                <ProductRegistrationStepper index={2} />
            </Flex>
            <Flex {...loginStyles.outerContainer}>
                <Flex {...loginStyles.accountDetails}>
                    {customer?.email && (
                        <>
                            <Text {...loginStyles.logoutText} data-testid="name">
                                <FormattedMessage
                                    id="product_registration_account.name"
                                    defaultMessage="<b>NAME</b>: {name}"
                                    values={{
                                        name: customer?.firstName || basket?.c_firstName,
                                        b: (chunks) => <b>{chunks}</b>,
                                    }}
                                />
                            </Text>
                            <Text {...loginStyles.logoutText} data-testid="surname">
                                <FormattedMessage
                                    id="product_registration_account.surname"
                                    defaultMessage="<b>SURNAME</b>: {surname}"
                                    values={{
                                        surname: customer?.lastName || basket?.c_lastName,
                                        b: (chunks) => <b>{chunks}</b>,
                                    }}
                                />
                            </Text>
                            <Text {...loginStyles.logoutText} data-testid="email">
                                <FormattedMessage
                                    id="product_registration_account.email"
                                    defaultMessage="<b>EMAIL</b>: {email}"
                                    values={{
                                        email: customer?.email || basket?.customerInfo?.email,
                                        b: (chunks) => <b>{chunks}</b>,
                                    }}
                                />
                            </Text>
                        </>
                    )}
                </Flex>
                <Divider {...loginStyles.horizontalDivider} />
                <Flex {...loginStyles.logoutContainer}>
                    <Text {...loginStyles.logoutText} data-testid="logout-message">
                        <FormattedMessage
                            id="product_registration_account.logout_message"
                            defaultMessage="If you want to register your product using another account, please <u>log out</u>"
                            values={{
                                u: (chunks) => (
                                    <u>
                                        <Button
                                            onClick={(e) => {
                                                e.preventDefault()
                                                handleLogout()
                                            }}
                                            style={{
                                                ...loginStyles.loginLink,
                                            }}
                                            variant={'text-button'}
                                            data-testid="logout-button"
                                        >
                                            {chunks}
                                        </Button>
                                    </u>
                                ),
                            }}
                        />
                    </Text>
                    <Button
                        {...loginStyles.button}
                        variant={'primary'}
                        onClick={handleButtonClick}
                        data-testid="purchase-details-button"
                    >
                        <Text {...loginStyles.buttonText}>
                            <FormattedMessage
                                id="product_registration_account.go_to_purchase_details"
                                defaultMessage="Go to purchase details"
                            />
                        </Text>
                    </Button>
                </Flex>
            </Flex>
        </Flex>
    )
}

export default ProductRegistrationAccount
